const RESERVED_KEYWORDS = ['rows', 'columns', 'schemas'];

/**
 * Resolves the first two required tokens: pageId and fieldPositionId.
 * Returns the page, fieldPosition, and field objects.
 */
const resolveBase = (doc, pageId, fieldPositionId) => {
  let page = null;
  for (const file of (doc.files || [])) {
    for (const p of (file.pages || [])) {
      if (p._id === pageId) {
        page = p;
        break;
      }
    }
    if (page) break;
  }
  if (!page) return null;

  const fp = (page.fieldPositions || []).find(fp => fp._id === fieldPositionId);
  if (!fp) return null;

  const field = (doc.fields || []).find(f => f._id === fp.field);
  if (!field) return null;

  const fileId = field.file || doc.files?.[0]?._id;

  return { page, fp, field, fileId };
};

/**
 * Finds the root schema key for a collection field.
 */
const getRootSchemaKey = (field) => {
  if (!field.schema) return null;
  return Object.keys(field.schema).find(key => field.schema[key]?.root) || null;
};

/**
 * Resolves the decorator slot from remaining path tokens after pageId/fieldPositionId.
 *
 * Returns an object:
 * {
 *   getArray: () => array reference (or []),
 *   setArray: (field, newArr) => mutated field (for write ops),
 *   fieldType: string
 * }
 *
 * Or null if path is invalid.
 */
const resolveSlot = (field, tokens) => {
  if (tokens.length === 0) {
    return {
      getArray: () => field.decorators || [],
      setArray: (f) => { if (!f.decorators) f.decorators = []; return f.decorators; },
      fieldType: field.type,
    };
  }

  const first = tokens[0];

  if (first === 'rows') {
    if (tokens.length !== 1) return null;

    if (field.type === 'collection') {
      const rootKey = getRootSchemaKey(field);
      if (!rootKey || !field.schema[rootKey]) return null;
      const schema = field.schema[rootKey];
      return {
        getArray: () => schema.rowDecorators || [],
        setArray: () => { if (!schema.rowDecorators) schema.rowDecorators = []; return schema.rowDecorators; },
        fieldType: field.type,
      };
    }

    return {
      getArray: () => field.rowDecorators || [],
      setArray: (f) => { if (!f.rowDecorators) f.rowDecorators = []; return f.rowDecorators; },
      fieldType: field.type,
    };
  }

  if (first === 'columns') {
    if (tokens.length !== 2) return null;
    const columnId = tokens[1];

    if (field.type === 'collection') {
      const rootKey = getRootSchemaKey(field);
      if (!rootKey || !field.schema[rootKey]) return null;
      const col = (field.schema[rootKey].tableColumns || []).find(c => c._id === columnId);
      if (!col) return null;
      return {
        getArray: () => col.decorators || [],
        setArray: () => { if (!col.decorators) col.decorators = []; return col.decorators; },
        fieldType: field.type,
      };
    }

    const col = (field.tableColumns || []).find(c => c._id === columnId);
    if (!col) return null;
    return {
      getArray: () => col.decorators || [],
      setArray: () => { if (!col.decorators) col.decorators = []; return col.decorators; },
      fieldType: field.type,
    };
  }

  if (first === 'schemas') {
    if (tokens.length < 3) return null;
    const schemaKey = tokens[1];
    const rest = tokens.slice(2);

    if (!field.schema || !field.schema[schemaKey]) return null;

    return resolveSchemaSlot(field, field.schema[schemaKey], rest);
  }

  // First token is a rowId (not a reserved keyword)
  const rowId = first;
  const rows = field.value;
  if (!Array.isArray(rows)) return null;

  const row = rows.find(r => r._id === rowId);
  if (!row) return null;

  const remaining = tokens.slice(1);

  if (remaining.length === 0) {
    // Determine the common row decorators for this field type
    // so we can inherit them on first initialization of row.decorators.all
    const getRowCommon = () => {
      if (field.type === 'collection') {
        const rk = getRootSchemaKey(field);
        return (rk && field.schema[rk]?.rowDecorators) || [];
      }
      return field.rowDecorators || [];
    };

    return {
      getArray: () => row.decorators?.all || getRowCommon().map(d => ({ ...d })),
      setArray: () => {
        if (!row.decorators) row.decorators = {};
        if (!row.decorators.all) {
          // First init: inherit common row decorators (same as FieldSettings.js:159-163)
          row.decorators.all = getRowCommon().map(d => ({ ...d }));
        }
        return row.decorators.all;
      },
      fieldType: field.type,
    };
  }

  if (remaining.length === 1 && remaining[0] !== 'schemas') {
    const columnId = remaining[0];
    if (RESERVED_KEYWORDS.includes(columnId)) return null;
    return {
      getArray: () => row.decorators?.cells?.[columnId] || [],
      setArray: () => {
        if (!row.decorators) row.decorators = {};
        if (!row.decorators.cells) row.decorators.cells = {};
        if (!row.decorators.cells[columnId]) row.decorators.cells[columnId] = [];
        return row.decorators.cells[columnId];
      },
      fieldType: field.type,
    };
  }

  if (remaining[0] === 'schemas') {
    if (remaining.length < 3) return null;
    const schemaKey = remaining[1];
    const rest = remaining.slice(2);

    if (!field.schema || !field.schema[schemaKey]) return null;

    const childValue = row.children?.[schemaKey]?.value;
    if (!Array.isArray(childValue)) return null;

    return resolveNestedCollectionSlot(field, field.schema[schemaKey], childValue, rest);
  }

  return null;
};

/**
 * Resolves slots within a schema definition (for schemas/{key}/... paths)
 */
const resolveSchemaSlot = (field, schema, tokens) => {
  const first = tokens[0];

  if (first === 'rows') {
    if (tokens.length !== 1) return null;
    return {
      getArray: () => schema.rowDecorators || [],
      setArray: () => { if (!schema.rowDecorators) schema.rowDecorators = []; return schema.rowDecorators; },
      fieldType: field.type,
    };
  }

  if (first === 'columns') {
    if (tokens.length !== 2) return null;
    const columnId = tokens[1];
    const col = (schema.tableColumns || []).find(c => c._id === columnId);
    if (!col) return null;
    return {
      getArray: () => col.decorators || [],
      setArray: () => { if (!col.decorators) col.decorators = []; return col.decorators; },
      fieldType: field.type,
    };
  }

  if (!schema.root) return null;
  if (RESERVED_KEYWORDS.includes(first)) return null;

  const rowId = first;
  const rows = field.value;
  if (!Array.isArray(rows)) return null;

  const row = rows.find(r => r._id === rowId);
  if (!row) return null;

  const remaining = tokens.slice(1);

  if (remaining.length === 0) {
    return {
      getArray: () => row.decorators?.all || (schema.rowDecorators || []).map(d => ({ ...d })),
      setArray: () => {
        if (!row.decorators) row.decorators = {};
        if (!row.decorators.all) {
          row.decorators.all = (schema.rowDecorators || []).map(d => ({ ...d }));
        }
        return row.decorators.all;
      },
      fieldType: field.type,
    };
  }

  if (remaining.length === 1 && !RESERVED_KEYWORDS.includes(remaining[0])) {
    const columnId = remaining[0];
    return {
      getArray: () => row.decorators?.cells?.[columnId] || [],
      setArray: () => {
        if (!row.decorators) row.decorators = {};
        if (!row.decorators.cells) row.decorators.cells = {};
        if (!row.decorators.cells[columnId]) row.decorators.cells[columnId] = [];
        return row.decorators.cells[columnId];
      },
      fieldType: field.type,
    };
  }

  return null;
};

/**
 * Resolves slots within nested collection row value arrays.
 * Handles: {nestedRowId}, {nestedRowId}/{columnId}, {nestedRowId}/schemas/{key}/...
 */
const resolveNestedCollectionSlot = (field, schema, valueArray, tokens) => {
  if (tokens.length === 0) return null;

  const rowId = tokens[0];
  const row = valueArray.find(r => r._id === rowId);
  if (!row) return null;

  const remaining = tokens.slice(1);

  if (remaining.length === 0) {
    return {
      getArray: () => row.decorators?.all || (schema.rowDecorators || []).map(d => ({ ...d })),
      setArray: () => {
        if (!row.decorators) row.decorators = {};
        if (!row.decorators.all) {
          // First init: inherit common row decorators from this schema level
          row.decorators.all = (schema.rowDecorators || []).map(d => ({ ...d }));
        }
        return row.decorators.all;
      },
      fieldType: field.type,
    };
  }

  if (remaining.length === 1 && remaining[0] !== 'schemas') {
    const columnId = remaining[0];
    if (RESERVED_KEYWORDS.includes(columnId)) return null;
    return {
      getArray: () => row.decorators?.cells?.[columnId] || [],
      setArray: () => {
        if (!row.decorators) row.decorators = {};
        if (!row.decorators.cells) row.decorators.cells = {};
        if (!row.decorators.cells[columnId]) row.decorators.cells[columnId] = [];
        return row.decorators.cells[columnId];
      },
      fieldType: field.type,
    };
  }

  if (remaining[0] === 'schemas') {
    if (remaining.length < 3) return null;
    const nestedSchemaKey = remaining[1];
    const rest = remaining.slice(2);

    if (!field.schema || !field.schema[nestedSchemaKey]) return null;

    if (rest.length > 0 && rest[0] === 'rows') {
      return resolveSchemaSlot(field, field.schema[nestedSchemaKey], rest);
    }

    if (rest.length > 0 && rest[0] === 'columns') {
      return resolveSchemaSlot(field, field.schema[nestedSchemaKey], rest);
    }

    const childValue = row.children?.[nestedSchemaKey]?.value;
    if (!Array.isArray(childValue)) {
      if (rest[0] === 'rows' || rest[0] === 'columns') {
        return resolveSchemaSlot(field, field.schema[nestedSchemaKey], rest);
      }
      return null;
    }

    return resolveNestedCollectionSlot(field, field.schema[nestedSchemaKey], childValue, rest);
  }

  return null;
};

/**
 * Full path resolution. Returns { slot, field, fileId } or null.
 */
export const resolvePath = (path, doc) => {
  if (!path || typeof path !== 'string') return null;

  const tokens = path.split('/').filter(Boolean);
  if (tokens.length < 2) return null;

  const pageId = tokens[0];
  const fieldPositionId = tokens[1];

  const base = resolveBase(doc, pageId, fieldPositionId);
  if (!base) return null;

  const remaining = tokens.slice(2);
  const slot = resolveSlot(base.field, remaining);
  if (!slot) return null;

  return { slot, field: base.field, fileId: base.fileId };
};

/**
 * DecoratorManager class for programmatic decorator CRUD on JoyDoc.
 * Users instantiate this and pass it as a prop to the JoyDoc component.
 *
 * @example
 * import { JoyDoc, DecoratorManager } from '@joyfill/components';
 *
 * const decoratorManager = new DecoratorManager();
 *
 * <JoyDoc decoratorManager={decoratorManager} doc={doc} mode="fill" />
 *
 * decoratorManager.getDecorators('pageId/fieldPositionId');
 * decoratorManager.addDecorators('pageId/fieldPositionId', [{ action: 'help', icon: 'info' }]);
 * decoratorManager.updateDecorator('pageId/fieldPositionId', 'help', { action: 'help', icon: 'star' });
 * decoratorManager.removeDecorator('pageId/fieldPositionId', 'help');
 */
class DecoratorManager {
  constructor() {
    this._handler = null;
  }

  /**
   * @private - Called by JoyDoc to register its internal handler.
   */
  _register(handler) {
    this._handler = handler;
  }

  /*
   * @private - Called by JoyDoc on unmount.
   */
  _unregister() {
    this._handler = null;
  }

  getDecorators(path) {
    if (!this._handler) {
      console.warn('DecoratorManager: No JoyDoc component connected');
      return [];
    }
    return this._handler('getDecorators', { path });
  }

  addDecorators(path, decorators) {
    if (!this._handler) {
      console.warn('DecoratorManager: No JoyDoc component connected');
      return;
    }
    return this._handler('addDecorators', { path, decorators });
  }

  updateDecorator(path, action, decorator) {
    if (!this._handler) {
      console.warn('DecoratorManager: No JoyDoc component connected');
      return;
    }
    return this._handler('updateDecorator', { path, action, decorator });
  }

  removeDecorator(path, action) {
    if (!this._handler) {
      console.warn('DecoratorManager: No JoyDoc component connected');
      return;
    }
    return this._handler('removeDecorator', { path, action });
  }
}

export default DecoratorManager;
